import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Zap, Crown, Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type ExamLabel = {
  id: string;
  slug: string;
  name: string;
};

type ExamPricing = {
  id: string;
  exam_slug: string;
  currency: string;
  period: string;
  interval_count: number;
  amount: number;
  display_price: string;
  savings: string | null;
};

const planFeatures = {
  monthly: ["Full MCQ database access", "Detailed explanations", "Progress tracking", "Email support"],
  quarterly: ["Full MCQ database access", "Detailed explanations", "Progress tracking", "Priority support", "Study analytics"],
  biannual: ["Full MCQ database access", "Detailed explanations", "Progress tracking", "Priority support", "Study analytics", "Mock exams"],
  annual: ["Full MCQ database access", "Detailed explanations", "Progress tracking", "Priority support", "Study analytics", "Mock exams", "Early access to new features"]
};

export const PricingPlans = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState<string | null>(null);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedExam, setSelectedExam] = useState<string>('medical-student');
  const [examLabels, setExamLabels] = useState<ExamLabel[]>([]);
  const [examPricing, setExamPricing] = useState<ExamPricing[]>([]);

  // Fetch exam labels
  useEffect(() => {
    const fetchExamLabels = async () => {
      const { data, error } = await supabase
        .from('exam_labels')
        .select('id, slug, name')
        .order('name');
      
      if (error) {
        console.error('Error fetching exam labels:', error);
        return;
      }
      
      setExamLabels(data || []);
      if (data && data.length > 0 && !selectedExam) {
        setSelectedExam(data[0].slug);
      }
    };

    fetchExamLabels();
  }, []);

  // Fetch pricing for selected exam
  useEffect(() => {
    const fetchPricing = async () => {
      if (!selectedExam) return;

      const { data, error } = await supabase
        .from('exam_pricing')
        .select('*')
        .eq('exam_slug', selectedExam)
        .order('interval_count');
      
      if (error) {
        console.error('Error fetching pricing:', error);
        return;
      }
      
      setExamPricing(data || []);
    };

    fetchPricing();
  }, [selectedExam]);

  const handleSubscribe = async (period: string) => {
    if (!user) {
      setShowAuthDialog(true);
      return;
    }

    setLoading(period);
    try {
      const { data, error } = await supabase.functions.invoke('create-exam-subscription', {
        body: { 
          exam_slug: selectedExam,
          subscription_period: period 
        }
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error: any) {
      console.error('Subscription error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create subscription",
        variant: "destructive",
      });
    } finally {
      setLoading(null);
    }
  };

  const selectedExamLabel = examLabels.find(e => e.slug === selectedExam);
  const currency = examPricing[0]?.currency?.toUpperCase() || 'USD';
  const currencySymbol = currency === 'SAR' ? 'SAR ' : currency === 'GBP' ? '£' : '$';

  return (
    <div className="py-12">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-foreground mb-4">
          Choose Your Exam Subscription
        </h2>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Start with a 3-day free trial, then choose the plan that fits your study timeline
        </p>
        
        {/* Exam Selector - always visible */}
        <div className="mt-8 flex justify-center">
          <Tabs value={selectedExam} onValueChange={setSelectedExam} className="w-auto">
            <TabsList className={`grid w-auto`} style={{ gridTemplateColumns: `repeat(${examLabels.length || 1}, minmax(120px, 1fr))` }}>
              {examLabels.map(exam => (
                <TabsTrigger key={exam.id} value={exam.slug}>
                  {exam.name}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        <div className="mt-6 p-4 bg-primary/10 rounded-lg inline-block">
          <p className="text-primary font-semibold">
            🎉 3-Day Free Trial • Cancel Anytime
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
        {examPricing.map((pricing, idx) => {
          const isMonthly = pricing.period === 'monthly';
          const isQuarterly = pricing.period === 'quarterly';
          const isBiannual = pricing.period === 'biannual';
          const isAnnual = pricing.period === 'annual';
          
          const Icon = isAnnual ? Crown : isBiannual ? Star : isQuarterly ? Crown : Zap;
          const color = isAnnual ? "from-amber-500 to-amber-600" : 
                       isBiannual ? "from-emerald-500 to-emerald-600" :
                       isQuarterly ? "from-purple-500 to-purple-600" : 
                       "from-blue-500 to-blue-600";
          
          const popular = isQuarterly;
          const badge = isAnnual ? "Best Value" : null;
          const features = planFeatures[pricing.period as keyof typeof planFeatures] || [];
          
          const monthlyPrice = pricing.amount / 100 / pricing.interval_count;
          const totalPrice = pricing.amount / 100;
          
          return (
            <Card 
              key={pricing.id}
              className={`relative transition-all duration-300 hover:scale-105 ${
                popular ? 'ring-2 ring-primary shadow-xl' : 'hover:shadow-lg'
              }`}
            >
              {popular && (
                <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary">
                  Most Popular
                </Badge>
              )}
              {badge && !popular && (
                <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-amber-500">
                  {badge}
                </Badge>
              )}
              
              <CardHeader className="text-center pb-2">
                <div className={`w-12 h-12 mx-auto rounded-full bg-gradient-to-r ${color} flex items-center justify-center mb-4`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <CardTitle className="text-xl">
                  {pricing.interval_count} Month{pricing.interval_count > 1 ? 's' : ''}
                </CardTitle>
                <CardDescription>
                  {isMonthly ? 'Perfect for short-term exam prep' :
                   isQuarterly ? 'Most popular for comprehensive prep' :
                   isBiannual ? 'Best value for extended study' :
                   'Ultimate value for serious students'}
                </CardDescription>
                
                <div className="mt-4">
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-3xl font-bold text-foreground">
                      {currencySymbol}{monthlyPrice.toFixed(2)}
                    </span>
                    <span className="text-muted-foreground">/month</span>
                  </div>
                  {pricing.savings && (
                    <div className="flex items-center justify-center gap-2 mt-1">
                      <span className="text-sm text-emerald-600 font-semibold">
                        {pricing.savings}
                      </span>
                    </div>
                  )}
                </div>
              </CardHeader>

              <CardContent className="pt-4">
                <ul className="space-y-3 mb-6">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      <span className="text-sm text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSubscribe(pricing.period)}
                  disabled={loading === pricing.period}
                  className={`w-full bg-gradient-to-r ${color} hover:opacity-90 transition-opacity`}
                >
                  {loading === pricing.period ? "Processing..." : "Start Free Trial"}
                </Button>
                
                <p className="text-xs text-muted-foreground text-center mt-3">
                  3-day free trial, then {currencySymbol}{totalPrice.toFixed(2)} total
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="text-center mt-12 space-y-2">
        <p className="text-muted-foreground">
          All plans include the same features during your trial period
        </p>
        {currency === 'SAR' && (
          <p className="text-sm text-muted-foreground">
            Prices shown in Saudi Riyal (SAR) inclusive of VAT
          </p>
        )}
      </div>

      <AlertDialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sign In Required</AlertDialogTitle>
            <AlertDialogDescription>
              You need to sign in or create an account to start your free trial.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setShowAuthDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => {
              setShowAuthDialog(false);
              navigate('/auth?returnTo=/pricing');
            }}>
              Sign In
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};